import React, { useState, useEffect, useRef } from "react";
import { 
  Box, 
  Typography, 
  TextField, 
  IconButton, 
  Paper, 
  Avatar, 
  Divider, 
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as AssistantIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  ShowChart as ChartIcon,
  Percent as PercentIcon,
  EuroSymbol as EuroIcon,
  Bolt as BoltIcon,
  Analytics as AnalyticsIcon,
  QueryStats as QueryStatsIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import { getToken, base_url } from "../../config/config";
import { tokens } from "../../theme";
import moment from "moment";
import 'chart.js/auto';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Styled components for AI Assistant Chat
const ChatContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 100px)",
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
}));

const MessageList = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: "auto",
  padding: theme.spacing(2),
  backgroundImage: theme.palette.mode === "dark" 
    ? "radial-gradient(rgba(0, 0, 0, 0.4) 1px, transparent 1px)"
    : "radial-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px)",
  backgroundSize: "20px 20px",
}));

const MessageInput = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const MessageBubbleUser = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "18px 18px 0 18px",
  maxWidth: "85%",
  marginLeft: "auto",
  marginRight: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  position: "relative",
  boxShadow: theme.shadows[2],
  wordBreak: "break-word",
}));

const MessageBubbleAssistant = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "18px 18px 18px 0",
  maxWidth: "85%",
  marginRight: "auto",
  marginLeft: theme.spacing(1),
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
  color: theme.palette.text.primary,
  position: "relative",
  boxShadow: theme.shadows[2],
  wordBreak: "break-word",
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

const PredictionCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  borderRadius: theme.shape.borderRadius,
  overflow: "visible",
  boxShadow: theme.shadows[2],
  border: `1px solid ${theme.palette.divider}`,
}));

const ConfidenceBar = styled(Box)(({ theme, confidence }) => ({
  position: "relative",
  height: 6,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[200],
  borderRadius: 3,
  marginTop: theme.spacing(1),
  "& .bar": {
    height: "100%",
    borderRadius: 3,
    width: `${confidence * 100}%`,
    backgroundColor: 
      confidence > 0.8 ? theme.palette.success.main :
      confidence > 0.5 ? theme.palette.warning.main : 
      theme.palette.error.main,
  }
}));

const PredictionValue = styled(Typography)(({ theme, isPrimary = false }) => ({
  fontSize: isPrimary ? '1.5rem' : '1.1rem',
  fontWeight: isPrimary ? 600 : 500,
  color: isPrimary 
    ? theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark 
    : theme.palette.text.primary,
}));

// Main component
const AIAssistantChat = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useSelector((state) => state.user?.user);
  
  // State
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);
  
  const messageListRef = useRef(null);
  
  // Load conversation history on mount
  useEffect(() => {
    fetchConversationHistory();
  }, []);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);
  
  const fetchConversationHistory = async () => {
    try {
      setInitialLoading(true);
      const response = await axios.get(`${base_url}/v1/ai-assistant/conversation`, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      console.log("Conversation history response:", response.data);
      
      // Accept any valid response data
      if (response.data) {
        // Safely extract messages, handling different response formats
        const fetchedMessages = response.data.message?.messages || 
                               response.data.messages || 
                               response.data.data?.messages || 
                               [];
        
        if (fetchedMessages && fetchedMessages.length > 0) {
          setMessages(fetchedMessages);
          setShowWelcomeMessage(false);
        } else {
          console.log("No messages in history or empty messages array");
        }
      } else {
        console.warn("Unexpected response format from conversation history endpoint:", response.data);
      }
    } catch (error) {
      console.error("Error fetching conversation history:", error);
    } finally {
      setInitialLoading(false);
    }
  };
  
  const sendMessage = async () => {
    if (!input.trim()) return;
    
    // Add user message to the UI immediately
    const userMessage = {
      role: 'user',
      content: input,
      createdAt: new Date().toISOString(),
      _id: `temp-${Date.now()}`
    };
    
    // Create a unique temporary ID for this message
    const tempMessageId = `temp-${Date.now()}`;
    userMessage._id = tempMessageId;
    
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    
    try {
      const apiUrl = `${base_url}/v1/ai-assistant/message`;
      console.log(`Sending request to ${apiUrl}`);
      
      const response = await axios.post(apiUrl, {
        message: input
      }, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      console.log("AI response:", response.data);
      
      // Check all possible response format variations
      if (response.data) {
        try {
          // Handle different response formats
          let userMsg, assistantMsg;
          
          if (response.data.message && typeof response.data.message === 'object') {
            if (response.data.message.userMessage && response.data.message.assistantMessage) {
              // Standard format
              userMsg = response.data.message.userMessage;
              assistantMsg = response.data.message.assistantMessage;
            } else {
              // Alternative format - log what we received
              console.log("Unexpected message format structure:", response.data.message);
              
              // Construct messages from what we have
              userMsg = {
                _id: `user-${Date.now()}`,
                role: 'user',
                content: input,
                createdAt: new Date().toISOString()
              };
              
              assistantMsg = {
                _id: `assistant-${Date.now()}`,
                role: 'assistant',
                content: typeof response.data.message === 'string' 
                  ? response.data.message 
                  : 'Die Antwort wurde erfolgreich empfangen, aber das Format war unerwartet.',
                createdAt: new Date().toISOString()
              };
            }
          } else {
            // Handle case where message might be a string
            console.log("Unexpected response.data.message format:", typeof response.data.message);
            
            // Construct default messages
            userMsg = {
              _id: `user-${Date.now()}`,
              role: 'user',
              content: input,
              createdAt: new Date().toISOString()
            };
            
            assistantMsg = {
              _id: `assistant-${Date.now()}`,
              role: 'assistant',
              content: typeof response.data.message === 'string' 
                ? response.data.message 
                : 'Die Antwort wurde erfolgreich empfangen, aber das Format war unerwartet.',
              createdAt: new Date().toISOString()
            };
          }
          
          // Update messages state with the real messages
          setMessages(prev => {
            // Filter out the temporary message we just added
            const filteredMessages = prev.filter(msg => msg._id !== tempMessageId);
            
            // Add the permanent messages
            return [
              ...filteredMessages,
              userMsg,
              assistantMsg
            ];
          });
          
          setShowWelcomeMessage(false);
        } catch (parseError) {
          console.error("Error parsing response:", parseError);
          // Add error message for parsing failure
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: "Es gab ein Problem beim Verarbeiten der Antwort. Details im Konsolenfenster.",
              createdAt: new Date().toISOString(),
              _id: `error-parse-${Date.now()}`
            }
          ]);
        }
      } else {
        console.error("Invalid response format:", response.data);
        // Add error message if response format is unexpected
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: "Die Serverantwort hatte ein unerwartetes Format. Bitte versuche es später erneut.",
            createdAt: new Date().toISOString(),
            _id: `error-format-${Date.now()}`
          }
        ]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      
      // Add error message
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "Es tut mir leid, aber ich konnte deine Nachricht nicht verarbeiten. Bitte versuche es später erneut.",
          createdAt: new Date().toISOString(),
          _id: `error-${Date.now()}`
        }
      ]);
    } finally {
      setLoading(false);
    }
  };
  
  const clearConversation = async () => {
    try {
      await axios.delete(`${base_url}/v1/ai-assistant/conversation`, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      setMessages([]);
      setShowWelcomeMessage(true);
    } catch (error) {
      console.error("Error clearing conversation:", error);
    }
  };
  
  // Render chart for prediction data
  const renderChart = (prediction) => {
    if (!prediction || !prediction.result || !prediction.result.breakdown) {
      return null;
    }
    
    const { breakdown } = prediction.result;
    
    if (breakdown.length === 0) {
      return null;
    }
    
    // Prepare chart data
    const labels = breakdown.map(item => item.label);
    const values = breakdown.map(item => item.value);
    
    // Chart configuration based on prediction type
    let chartComponent = null;
    
    switch (prediction.type) {
      case 'profit':
        // Bar chart for profit
        chartComponent = (
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: 'Euro (€)',
                  data: values,
                  backgroundColor: values.map(v => 
                    v >= 0 
                      ? isDark ? colors.greenAccent[500] : colors.greenAccent[400]
                      : isDark ? colors.redAccent[500] : colors.redAccent[400]
                  ),
                  borderColor: values.map(v => 
                    v >= 0 
                      ? isDark ? colors.greenAccent[700] : colors.greenAccent[600]
                      : isDark ? colors.redAccent[700] : colors.redAccent[600]
                  ),
                  borderWidth: 1,
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  grid: {
                    color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  },
                  ticks: {
                    color: isDark ? colors.grey[300] : colors.grey[700],
                  }
                },
                x: {
                  grid: {
                    display: false
                  },
                  ticks: {
                    color: isDark ? colors.grey[300] : colors.grey[700],
                  }
                }
              },
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.parsed.y.toLocaleString('de-DE')} €`
                  }
                }
              }
            }}
            height={200}
          />
        );
        break;
        
      case 'project_success':
        // Radar or Pie chart for project success factors
        chartComponent = (
          <Pie
            data={{
              labels,
              datasets: [
                {
                  data: values.map(v => v * 100), // Convert to percentage
                  backgroundColor: [
                    isDark ? colors.greenAccent[500] : colors.greenAccent[400],
                    isDark ? colors.blueAccent[500] : colors.blueAccent[400],
                    isDark ? colors.redAccent[500] : colors.redAccent[400],
                  ],
                  borderColor: isDark ? colors.primary[400] : colors.grey[100],
                  borderWidth: 1,
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    color: isDark ? colors.grey[300] : colors.grey[700],
                  }
                },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.label}: ${context.parsed.toFixed(1)}%`
                  }
                }
              }
            }}
            height={200}
          />
        );
        break;
        
      case 'machine_hours':
        // Bar chart for machine hours
        chartComponent = (
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: 'Stunden',
                  data: values,
                  backgroundColor: isDark ? colors.blueAccent[500] : colors.blueAccent[400],
                  borderColor: isDark ? colors.blueAccent[700] : colors.blueAccent[600],
                  borderWidth: 1,
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  grid: {
                    color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  },
                  ticks: {
                    color: isDark ? colors.grey[300] : colors.grey[700],
                  }
                },
                x: {
                  grid: {
                    display: false
                  },
                  ticks: {
                    color: isDark ? colors.grey[300] : colors.grey[700],
                  }
                }
              },
              plugins: {
                legend: {
                  display: false
                }
              }
            }}
            height={200}
          />
        );
        break;
      
      default:
        // Line chart for any other prediction type
        chartComponent = (
          <Line
            data={{
              labels,
              datasets: [
                {
                  label: prediction.result.unit,
                  data: values,
                  backgroundColor: isDark ? colors.primary[500] : colors.primary[400],
                  borderColor: isDark ? colors.primary[400] : colors.primary[500],
                  tension: 0.3,
                  fill: false,
                  pointBackgroundColor: isDark ? colors.greenAccent[500] : colors.greenAccent[400],
                  pointBorderColor: '#fff',
                  pointBorderWidth: 2,
                  pointRadius: 4,
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  grid: {
                    color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  },
                  ticks: {
                    color: isDark ? colors.grey[300] : colors.grey[700],
                  }
                },
                x: {
                  grid: {
                    display: false
                  },
                  ticks: {
                    color: isDark ? colors.grey[300] : colors.grey[700],
                  }
                }
              }
            }}
            height={200}
          />
        );
    }
    
    return (
      <Box sx={{ height: 260, mt: 2, mb: 2, p: 1 }}>
        {chartComponent}
      </Box>
    );
  };
  
  // Render prediction card for AI predictions
  const renderPredictionCard = (metadata) => {
    if (!metadata || !metadata.resultData || metadata.queryType !== 'prediction') {
      return null;
    }
    
    const prediction = metadata.resultData;
    
    return (
      <PredictionCard>
        <CardContent>
          {/* Prediction header with confidence */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AnalyticsIcon 
                sx={{ 
                  mr: 1, 
                  color: isDark ? colors.primary[400] : colors.primary[500],
                  fontSize: '1.5rem'
                }} 
              />
              <Typography variant="h6" fontWeight={600}>
                {prediction.type === 'profit' ? 'Gewinnprognose' :
                 prediction.type === 'project_success' ? 'Projekterfolgsanalyse' :
                 prediction.type === 'machine_hours' ? 'Maschinenstundenprognose' :
                 'Prognose'}
              </Typography>
            </Box>
            <Chip
              label={`${Math.round(prediction.confidence * 100)}% Konfidenz`}
              size="small"
              color={
                prediction.confidence > 0.8 ? 'success' :
                prediction.confidence > 0.5 ? 'warning' :
                'error'
              }
              sx={{ fontWeight: 500 }}
            />
          </Box>
          
          <Divider sx={{ my: 1.5 }} />
          
          {/* Prediction value display */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {prediction.type === 'profit' ? (
                    <EuroIcon 
                      sx={{ 
                        mr: 1, 
                        color: isDark ? colors.greenAccent[500] : colors.greenAccent[600],
                        fontSize: '2rem'
                      }} 
                    />
                  ) : prediction.type === 'project_success' ? (
                    <PercentIcon 
                      sx={{ 
                        mr: 1, 
                        color: isDark ? colors.blueAccent[500] : colors.blueAccent[600],
                        fontSize: '2rem'
                      }} 
                    />
                  ) : (
                    <TimeIcon 
                      sx={{ 
                        mr: 1, 
                        color: isDark ? colors.primary[400] : colors.primary[600],
                        fontSize: '2rem'
                      }} 
                    />
                  )}
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {prediction.type === 'profit' ? 'Prognostizierter Gewinn' :
                       prediction.type === 'project_success' ? 'Erfolgswahrscheinlichkeit' :
                       prediction.type === 'machine_hours' ? 'Prognostizierte Stunden' :
                       'Prognostizierter Wert'}
                    </Typography>
                    <PredictionValue isPrimary={true}>
                      {prediction.type === 'profit' 
                        ? `${prediction.result.value.toLocaleString('de-DE')} €` 
                        : prediction.type === 'project_success'
                        ? `${Math.round(prediction.result.value * 100)}%`
                        : prediction.type === 'machine_hours'
                        ? `${prediction.result.value} Stunden`
                        : `${prediction.result.value} ${prediction.result.unit}`}
                    </PredictionValue>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Zeitraum
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimeIcon sx={{ fontSize: '1rem', mr: 0.5, color: isDark ? colors.grey[400] : colors.grey[600] }} />
                    <Typography variant="body2">
                      {moment(prediction.timePeriod.start).format('DD.MM.YYYY')} - {moment(prediction.timePeriod.end).format('DD.MM.YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Render chart for visual representation */}
          {renderChart(prediction)}
          
          {/* Influencing factors if available */}
          {prediction.influencingFactors && prediction.influencingFactors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Einflussfaktoren:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {prediction.influencingFactors.map((factor, index) => (
                  <Chip
                    key={index}
                    label={factor.factor}
                    size="small"
                    icon={
                      factor.impact === 'positive' ? <BoltIcon style={{ color: colors.greenAccent[500] }} /> :
                      factor.impact === 'negative' ? <BoltIcon style={{ color: colors.redAccent[500] }} /> :
                      <BoltIcon style={{ color: colors.grey[500] }} />
                    }
                    variant="outlined"
                    sx={{ 
                      borderColor: 
                        factor.impact === 'positive' ? colors.greenAccent[400] :
                        factor.impact === 'negative' ? colors.redAccent[400] :
                        colors.grey[400],
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </PredictionCard>
    );
  };
  
  // Render data query visualization
  const renderDataQueryResult = (metadata) => {
    if (!metadata || !metadata.resultData || metadata.queryType !== 'data_query') {
      return null;
    }
    
    const result = metadata.resultData;
    
    if (result.type === 'count') {
      return (
        <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: isDark ? colors.primary[400] : colors.grey[100], borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QueryStatsIcon sx={{ mr: 1, color: colors.blueAccent[400] }} />
            <Typography variant="h5" fontWeight={600}>
              {result.value}
            </Typography>
            <Typography variant="body1" sx={{ ml: 1 }}>
              {result.entity === 'project' ? 'Projekte' : 
               result.entity === 'invoice' ? 'Rechnungen' : 
               result.entity}
            </Typography>
          </Box>
        </Box>
      );
    }
    
    if (result.type === 'list' && result.items && result.items.length > 0) {
      return (
        <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: isDark ? colors.primary[400] : colors.grey[100], borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {result.items.length} {result.entity === 'project' ? 'Projekte' : 
                                  result.entity === 'invoice' ? 'Rechnungen' : 
                                  result.entity}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {result.items.map((item, index) => (
            <Box 
              key={index} 
              sx={{ 
                py: 1, 
                borderBottom: index < result.items.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` : 'none'
              }}
            >
              <Typography variant="body2">
                {index + 1}. {item.name || item.title || item.anrId || 'Unnamed'}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    
    if (result.type === 'aggregate') {
      return (
        <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: isDark ? colors.primary[400] : colors.grey[100], borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EuroIcon sx={{ mr: 1, color: colors.greenAccent[400] }} />
            <Typography variant="h5" fontWeight={600}>
              {result.value.toLocaleString('de-DE')} €
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {result.entity === 'invoice' && result.metric === 'total_paid' ? 'Summe aller bezahlten Rechnungen' : 'Gesamtwert'}
          </Typography>
        </Box>
      );
    }
    
    if (result.type === 'machine_hours' && result.machines) {
      // Simple bar chart for machine hours
      const labels = result.machines.map(m => m.name);
      const values = result.machines.map(m => m.hours);
      
      return (
        <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: isDark ? colors.primary[400] : colors.grey[100], borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Maschinenstunden
          </Typography>
          <Box sx={{ height: 200, mt: 2 }}>
            <Bar
              data={{
                labels,
                datasets: [
                  {
                    label: 'Stunden',
                    data: values,
                    backgroundColor: isDark ? colors.blueAccent[500] : colors.blueAccent[400],
                    borderColor: isDark ? colors.blueAccent[700] : colors.blueAccent[600],
                    borderWidth: 1,
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: isDark ? colors.grey[300] : colors.grey[700],
                    }
                  },
                  x: {
                    grid: {
                      display: false
                    },
                    ticks: {
                      color: isDark ? colors.grey[300] : colors.grey[700],
                    }
                  }
                },
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }}
            />
          </Box>
        </Box>
      );
    }
    
    return null;
  };
  
  // Format message content with special handling
  const formatMessageContent = (message) => {
    // Check if this is an assistant message with metadata
    if (message.role === 'assistant' && message.metadata) {
      return (
        <>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
          
          {/* Render prediction card if this is a prediction response */}
          {message.metadata.queryType === 'prediction' && renderPredictionCard(message.metadata)}
          
          {/* Render data visualization if this is a data query response */}
          {message.metadata.queryType === 'data_query' && renderDataQueryResult(message.metadata)}
        </>
      );
    }
    
    // Default text formatting
    return (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
        {message.content}
      </Typography>
    );
  };
  
  // Render welcome message
  const renderWelcomeMessage = () => {
    if (!showWelcomeMessage) return null;
    
    return (
      <Box sx={{ textAlign: 'center', my: 4 }}>
        <Avatar 
          sx={{ 
            width: 80, 
            height: 80, 
            margin: '0 auto', 
            bgcolor: colors.blueAccent[500],
            mb: 2
          }}
        >
          <AssistantIcon sx={{ fontSize: 50 }} />
        </Avatar>
        <Typography variant="h5" gutterBottom>
          Willkommen bei Ihrem KI-Assistenten
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
          Ich kann Ihnen mit Datenabfragen, Prognosen und allgemeinen Informationen helfen.
          Stellen Sie mir eine Frage, um zu beginnen.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500, mx: 'auto' }}>
          <Button 
            variant="outlined" 
            onClick={() => setInput("Wie viele aktive Projekte haben wir?")}
            startIcon={<QueryStatsIcon />}
            sx={{ justifyContent: 'flex-start', py: 1 }}
          >
            Wie viele aktive Projekte haben wir?
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setInput("Was wird unser Gewinn im nächsten Monat sein?")}
            startIcon={<ChartIcon />}
            sx={{ justifyContent: 'flex-start', py: 1 }}
          >
            Was wird unser Gewinn im nächsten Monat sein?
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setInput("Wie viele Maschinenstunden werden wir im kommenden Monat benötigen?")}
            startIcon={<TimeIcon />}
            sx={{ justifyContent: 'flex-start', py: 1 }}
          >
            Wie viele Maschinenstunden werden wir im kommenden Monat benötigen?
          </Button>
        </Box>
      </Box>
    );
  };
  
  return (
    <ChatContainer>
      <ChatHeader>
        <AssistantIcon sx={{ mr: 2, fontSize: 28 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">KI-Assistent</Typography>
          <Typography variant="caption">Prognosen, Datenanalyse und Hilfe</Typography>
        </Box>
        <Tooltip title="Konversation löschen">
          <IconButton color="inherit" onClick={clearConversation}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </ChatHeader>
      
      <MessageList ref={messageListRef}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {renderWelcomeMessage()}
            
            {messages.map((message, index) => {
              const isUser = message.role === 'user';
              return (
                <Box 
                  key={message._id || index} 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: isUser ? 'flex-end' : 'flex-start',
                    mb: 3 
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>
                    {!isUser && (
                      <Avatar 
                        sx={{ 
                          bgcolor: colors.blueAccent[500],
                          width: 32,
                          height: 32,
                          mr: 1
                        }}
                      >
                        <AssistantIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                    )}
                    
                    {isUser ? (
                      <MessageBubbleUser>
                        {formatMessageContent(message)}
                      </MessageBubbleUser>
                    ) : (
                      <MessageBubbleAssistant>
                        {formatMessageContent(message)}
                      </MessageBubbleAssistant>
                    )}
                    
                    {isUser && (
                      <Avatar 
                        sx={{ 
                          bgcolor: colors.primary.main,
                          width: 32,
                          height: 32,
                          ml: 1
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                    )}
                  </Box>
                  
                  <Typography 
                    variant="caption" 
                    color="textSecondary"
                    sx={{ 
                      mx: 2,
                      alignSelf: isUser ? 'flex-end' : 'flex-start'
                    }}
                  >
                    {moment(message.createdAt).format('HH:mm · DD.MM.YYYY')}
                  </Typography>
                </Box>
              );
            })}
            
            {loading && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  my: 2
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: colors.blueAccent[500],
                    width: 32,
                    height: 32,
                    mr: 1
                  }}
                >
                  <AssistantIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box 
                  sx={{ 
                    px: 2, 
                    py: 1.5, 
                    bgcolor: isDark ? colors.primary[400] : colors.grey[100],
                    borderRadius: '18px 18px 18px 0',
                    minWidth: 100
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: colors.primary.main,
                        mr: 1,
                        animation: 'pulse 1.2s infinite',
                        '@keyframes pulse': {
                          '0%': {
                            opacity: 0.5,
                            transform: 'scale(0.8)',
                          },
                          '50%': {
                            opacity: 1,
                            transform: 'scale(1.1)',
                          },
                          '100%': {
                            opacity: 0.5,
                            transform: 'scale(0.8)',
                          },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: colors.primary.main,
                        mr: 1,
                        animation: 'pulse 1.2s infinite 0.2s',
                        '@keyframes pulse': {
                          '0%': {
                            opacity: 0.5,
                            transform: 'scale(0.8)',
                          },
                          '50%': {
                            opacity: 1,
                            transform: 'scale(1.1)',
                          },
                          '100%': {
                            opacity: 0.5,
                            transform: 'scale(0.8)',
                          },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: colors.primary.main,
                        animation: 'pulse 1.2s infinite 0.4s',
                        '@keyframes pulse': {
                          '0%': {
                            opacity: 0.5,
                            transform: 'scale(0.8)',
                          },
                          '50%': {
                            opacity: 1,
                            transform: 'scale(1.1)',
                          },
                          '100%': {
                            opacity: 0.5,
                            transform: 'scale(0.8)',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </MessageList>
      
      <MessageInput>
        <TextField
          fullWidth
          placeholder="Stellen Sie eine Frage oder bitten Sie um eine Prognose..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          multiline
          maxRows={3}
          disabled={loading}
          sx={{ mr: 2 }}
          InputProps={{
            sx: { borderRadius: '24px' }
          }}
        />
        <IconButton 
          color="primary" 
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          sx={{ 
            bgcolor: input.trim() ? colors.primary.main : 'transparent',
            color: input.trim() ? '#fff' : undefined,
            '&:hover': {
              bgcolor: input.trim() ? colors.primary.dark : undefined,
            },
            '&.Mui-disabled': {
              bgcolor: 'transparent',
            }
          }}
        >
          {loading ? <CircularProgress size={24} /> : <SendIcon />}
        </IconButton>
      </MessageInput>
    </ChatContainer>
  );
};

export default AIAssistantChat;